import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useEditProfileModal } from "../../contexts/useEditProfileModal";
import { useUserStore, canEditSelectedProfile } from "../../store/user";
import logoImage from "../../assets/images/logo-georanking.png";
import {
  SidebarContainer,
  SidebarHeader,
  Logo,
  MenusWrapper,
  MenuSection,
  MenuTitle,
  MenuItem,
  MenuIcon,
  MenuLabel,
  MenuBadge,
  SidebarFooter,
  LogoutButton,
  MobileMenuButton,
  Overlay,
} from "./styles";

interface SidebarProps {
  activeItem?: string;
}

export const Sidebar: React.FC<SidebarProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openModal } = useEditProfileModal();
  const { clearUser, user } = useUserStore();
  const canEditProfile = canEditSelectedProfile(user);
  const [isOpen, setIsOpen] = useState(false);

  // Fecha o menu quando a rota muda
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Previne scroll do body quando o menu está aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  interface MenuItem {
    id: string;
    label: string;
    icon: string;
    path: string;
    badge: null | string;
    isCustom?: boolean;
  }

  const menuItems: MenuItem[] = [
    {
      id: "home",
      label: "Início",
      icon: "home",
      path: "/home",
      badge: null,
    },
    // Cliente com permissao VIEW no perfil selecionado nao ve "Editar Perfil".
    ...(canEditProfile
      ? [
          {
            id: "edit-profile",
            label: "Editar Perfil",
            icon: "edit",
            path: "/edit-profile",
            badge: null,
            isCustom: true,
          },
        ]
      : []),
    {
      id: "georanking",
      label: "Novo Perfil",
      icon: "add_location",
      path: "/georanking",
      badge: null,
    },
    {
      id: "reviews",
      label: "Comentários",
      icon: "comment",
      path: "/reviews",
      badge: null,
    },
    {
      id: "posts",
      label: "Postagens",
      icon: "post_add",
      path: "/posts",
      badge: null,
    },
    {
      id: "reports-keywords",
      label: "Relatório de Palavras-chave",
      icon: "assessment",
      path: "/reports/keywords",
      badge: null,
    },
    {
      id: "reports-performance",
      label: "Relatório de Performance",
      icon: "trending_up",
      path: "/reports/performance",
      badge: null,
    },
    ...(import.meta.env.VITE_BACKLINKS_DASHBOARD === "true"
      ? [
          {
            id: "backlinks-dashboard",
            label: "Backlinks",
            icon: "link",
            path: "/backlinks-dashboard",
            badge: null,
          },
        ]
      : []),
  ];

  const secondaryItems = [
    {
      id: "change-password",
      label: "Trocar senha",
      icon: "lock",
      path: "/change-password",
    },
    {
      id: "help",
      label: "Ajuda",
      icon: "help",
      path: "/help",
    },
  ];

  const handleNavigation = (path: string, isCustom?: boolean, itemId?: string) => {
    if (isCustom && itemId === "edit-profile") {
      // Usa sempre o contexto global para abrir o modal
      openModal();
      setIsOpen(false);
      return;
    }
    
    navigate(path);
    setIsOpen(false);
  };

  const handleLogout = () => {
    clearUser();
    localStorage.removeItem("user-storage");
    navigate("/login");
  };

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const isActive = (path: string) => {
    if (path === "/reports/keywords") {
      return location.pathname.startsWith("/reports/keywords");
    }
    if (path === "/reports/performance") {
      return location.pathname === "/reports/performance";
    }
    return location.pathname === path;
  };

  return (
    <>
      <MobileMenuButton onClick={toggleSidebar}>
        <span className="material-icons">
          {isOpen ? "close" : "menu"}
        </span>
      </MobileMenuButton>

      <Overlay $isOpen={isOpen} onClick={toggleSidebar} />

      <SidebarContainer $isOpen={isOpen}>
        <SidebarHeader>
          <Logo as="img" src={logoImage} alt="GeoRanking Logo" />
        </SidebarHeader>

        <MenusWrapper>
          <MenuSection>
            <MenuTitle>MENU PRINCIPAL</MenuTitle>
            {menuItems.map((item) => (
              <MenuItem
                key={item.id}
                $active={isActive(item.path)}
                onClick={() => handleNavigation(item.path, item.isCustom, item.id)}
              >
                <MenuIcon className="material-icons">{item.icon}</MenuIcon>
                <MenuLabel>{item.label}</MenuLabel>
                {item.badge && <MenuBadge>{item.badge}</MenuBadge>}
              </MenuItem>
            ))}
          </MenuSection>

          <MenuSection>
            <MenuTitle>OUTROS</MenuTitle>
            {secondaryItems.map((item) => (
              <MenuItem
                key={item.id}
                $active={isActive(item.path)}
                onClick={() => handleNavigation(item.path)}
              >
                <MenuIcon className="material-icons">{item.icon}</MenuIcon>
                <MenuLabel>{item.label}</MenuLabel>
              </MenuItem>
            ))}
          </MenuSection>
        </MenusWrapper>

        <SidebarFooter>
          <LogoutButton onClick={handleLogout}>
            <span className="material-icons">logout</span>
            Sair
          </LogoutButton>
        </SidebarFooter>
      </SidebarContainer>
    </>
  );
};
